import { lazy, Suspense, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useLocale } from "./i18n/useLocale";
import { IntlBoot } from "./i18n/IntlBoot";
import { usePMSession } from "./hooks/usePMSession";
import { LocalePicker } from "./screens/LocalePicker";
import { Login } from "./screens/Login";
import { ForgotPassword } from "./screens/ForgotPassword";
import { HomeShell } from "./screens/HomeShell";
import { Account } from "./screens/Account";
import { MapScreen } from "./screens/MapScreen";
import { WorkersPage } from "./features/workers/WorkersPage";
import { QueueHealthPage } from "./pages/admin/QueueHealth";
import { ClockSkewPage } from "./pages/admin/ClockSkew";
// Phase 7 Plan 03 (D-150, EXPORT-10.*) — /exports page + (D-164) /admin/outdated-apk page.
import { ExportsPage } from "./pages/exports/ExportsPage";
import { OutdatedApkPage } from "./pages/admin/OutdatedApk";
// Phase 6 Plan 02 (D-138, CADENCE-6.1) — /settings route.
import { CleaningCadencePage } from "./pages/settings/CleaningCadence";

// Plan 04 ships an ImportWizardPage stub at this path; Plan 05 (Wave 4)
// overwrites with the real 3-step wizard. Lazy-loaded so the map screen's
// initial bundle doesn't carry the wizard's deps.
const ImportWizardPage = lazy(() =>
  import("./features/import/ImportWizardPage").then((m) => ({ default: m.ImportWizardPage })),
);

/**
 * LocaleGuard — redirects to /picker if localStorage("storm-locale") is null.
 * Lives inside IntlBoot so react-intl is already available.
 * Must stay ABOVE PMOnly in the tree (locale gate fires first).
 */
function LocaleGuard({ children }: { children: ReactNode }) {
  const locale = useLocale();
  if (locale === null) return <Navigate to="/picker" replace />;
  return <>{children}</>;
}

/**
 * PMOnly — redirects to /login if the server session is missing.
 * Renders nothing while the initial /me probe is in-flight or while a
 * retry is being attempted. After retries are exhausted (W4-002 patch in
 * usePMSession), shows an inline transient-error card with a refetch CTA
 * instead of bouncing to /login — the cookie session may still be valid;
 * a 502 during a deploy looked identical to "session expired" pre-fix.
 */
function PMOnly({ children }: { children: ReactNode }) {
  const { data: pm, isLoading, isError, isFetching, refetch } = usePMSession();
  if (isLoading || isFetching) return null;
  if (isError) {
    return (
      <div
        role="alert"
        style={{
          maxWidth: "32rem",
          margin: "4rem auto",
          padding: "1.5rem",
          textAlign: "center",
          border: "1px solid var(--border-default, #e5e7eb)",
          borderRadius: "0.5rem",
        }}
      >
        <p style={{ marginBottom: "1rem" }}>
          <FormattedMessage id="code_ERR_NETWORK" />
        </p>
        <button
          type="button"
          onClick={() => {
            void refetch();
          }}
          style={{
            padding: "0.5rem 1rem",
            border: "1px solid var(--border-default, #d1d5db)",
            borderRadius: "0.375rem",
            background: "var(--surface-primary, #ffffff)",
            cursor: "pointer",
          }}
        >
          <FormattedMessage id="code_LABEL_TRY_AGAIN" />
        </button>
      </div>
    );
  }
  if (pm === null || pm === undefined) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * PrivateLayout — wraps every authenticated route in HomeShell + PMOnly guard.
 * React Router <Outlet> renders the matched child route inside HomeShell.
 */
function PrivateLayout() {
  return (
    <PMOnly>
      <HomeShell />
    </PMOnly>
  );
}

export function App() {
  return (
    // IntlBoot must be INSIDE QueryClientProvider (mounted in main.tsx)
    // so that locale changes do NOT clear the Query cache (RESEARCH § 10).
    <IntlBoot>
      <BrowserRouter>
        <Routes>
          {/* /picker — first-visit locale gate; no auth required */}
          <Route path="/picker" element={<LocalePicker />} />

          {/* All other routes require a locale to be set */}
          <Route element={<LocaleGuard><Outlet /></LocaleGuard>}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Private authenticated routes inside HomeShell layout */}
            <Route element={<PrivateLayout />}>
              <Route index element={<MapScreen />} />
              <Route
                path="/assets/import"
                element={
                  <Suspense fallback={null}>
                    <ImportWizardPage />
                  </Suspense>
                }
              />
              <Route path="/account" element={<Account />} />
              <Route path="/workers" element={<WorkersPage />} />
              {/* Phase 5 Plan 05 — PM admin tiles (D-121, D-128). */}
              <Route path="/admin/queue-health" element={<QueueHealthPage />} />
              <Route path="/admin/clock-skew" element={<ClockSkewPage />} />
              {/* Phase 6 Plan 02 — PM cadence settings page (D-138, CADENCE-6.1). */}
              <Route path="/settings" element={<CleaningCadencePage />} />
              {/* Phase 7 Plan 03 (D-150, EXPORT-10.*) — /exports + (D-164) outdated APK. */}
              <Route path="/exports" element={<ExportsPage />} />
              <Route path="/admin/outdated-apk" element={<OutdatedApkPage />} />
            </Route>
          </Route>

          {/* Catch-all → /picker */}
          <Route path="*" element={<Navigate to="/picker" replace />} />
        </Routes>
      </BrowserRouter>
    </IntlBoot>
  );
}
